import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Layout, Button, Grid, Dropdown } from "antd";
import { UserOutlined, LogoutOutlined, EnvironmentOutlined, HomeOutlined, SearchOutlined, ThunderboltOutlined, UnorderedListOutlined } from "@ant-design/icons";
import Sidenav from "./Sidenav";
import MobileSideMenu from "./MobileSideMenu";
import Header from "./Header";
import Footer from "./Footer";
import FeedbackModal from "../common/FeedbackModal";
import { fetchUserDetails } from "../../pages/Dashboard/services/api";

const { Header: AntHeader, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

function Main({ children }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [mobileDealerLocation, setMobileDealerLocation] = useState("");
  const screens = useBreakpoint();
  const { pathname } = useLocation();
  const history = useHistory();
  const { isSignedIn, isLoaded } = useAuth();

  // Determine if we're on mobile based on md breakpoint
  const isMobile = !screens.md;

  // Fetch dealer location for mobile header
  useEffect(() => {
    if (!isMobile) return;
    fetchUserDetails()
      .then((data) => {
        const dealershipObj = data.dealership || {};
        const dealershipDetails = data.dealership_details || {};
        const city = data.dealership_city || data.city || dealershipObj.city || dealershipDetails.city || "";
        const state = data.dealership_state || data.state || dealershipObj.state || dealershipDetails.state || "";
        if (city && state) setMobileDealerLocation(`${city}, ${state}`);
        else if (city) setMobileDealerLocation(city);
        else if (state) setMobileDealerLocation(state);
        else setMobileDealerLocation("Location");
      })
      .catch(() => setMobileDealerLocation("Location"));
  }, [isMobile]);

  const getPageName = (path) => {
    const parts = path.split("/").filter(Boolean);
    if (!parts.length) return "Home";
    const section = parts[0].toLowerCase();
    const map = {
      insights: "Insights",
      search: "Search",
      dashboard: "Home",
      "private-listings": "Private Listings",
      "search-insights": "Market Search",
      "dealer-insights": "Lot Performance",
      recommendations: "Recommended Listings",
      "buy-signals": "Buy Signals",
      profile: "Account",
      settings: "Settings",
    };
    return map[section] || section.charAt(0).toUpperCase() + section.slice(1);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile, isMobileMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [pathname, isMobile]);

  // Show feedback modal randomly and infrequently
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    // Don't show on onboarding or support pages
    if (pathname.includes('/onboarding') || pathname.includes('/support')) {
      return;
    }

    // Check if user already completed feedback
    const feedbackCompleted = localStorage.getItem('hooptie_feedback_completed');
    if (feedbackCompleted === 'true') {
      return;
    }

    // Check if user dismissed recently (wait 7 days before showing again)
    const feedbackDismissed = localStorage.getItem('hooptie_feedback_dismissed');
    if (feedbackDismissed) {
      const dismissedDate = new Date(feedbackDismissed);
      const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Check if we've already shown feedback in this session
    const sessionShown = sessionStorage.getItem('hooptie_feedback_shown');
    if (sessionShown === 'true') {
      return;
    }

    // Random chance to show (20% chance)
    // Also wait a bit after page load (5-10 seconds) so user has time to use the app
    const shouldShow = Math.random() < 0.20;
    if (shouldShow) {
      const delay = 5000 + Math.random() * 5000; // 5-10 seconds
      const timer = setTimeout(() => {
        setShowFeedbackModal(true);
        sessionStorage.setItem('hooptie_feedback_shown', 'true');
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isLoaded, isSignedIn, pathname]);

  if (isMobile) {
    const navItems = [
      { path: "/dashboard", label: "Home", icon: HomeOutlined },
      { path: "/search", label: "Search", icon: SearchOutlined },
      { path: "/buy-signals", label: "Signals", icon: ThunderboltOutlined },
      { path: "/dealer-insights", label: "Lot", icon: UnorderedListOutlined },
    ];
    const isActive = (path) => pathname === path || (path === "/dashboard" && (pathname === "/" || pathname === "/dashboard"));

    return (
      <Layout className="theme-dark layout-dashboard" style={{ minHeight: "100vh", overflow: "visible" }}>
        {/* Mobile Side Menu */}
        <MobileSideMenu 
          isOpen={isMobileMenuOpen} 
          onClose={handleMobileMenuClose} 
        />

        {/* Mobile Layout */}
        <Layout style={{ minHeight: "100vh", marginLeft: 0, overflow: "visible" }}>
          <AntHeader className="main-header mobile-header" style={{ background: "#0d1117", padding: "0 16px", height: "48px", lineHeight: "48px", minHeight: "48px", borderBottom: "1px solid rgba(255,255,255,0.5)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <div className="mobile-header-brand" style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: "16px", fontWeight: 600, color: "#ffffff" }}>Hooptie</span>
              </div>
              <Dropdown
                overlayClassName="mobile-logout-dropdown"
                menu={{
                  items: [
                    {
                      key: "logout",
                      label: "Logout",
                      icon: <LogoutOutlined />,
                      onClick: () => {
                        if (window.Clerk?.signOut) {
                          window.Clerk.signOut();
                        }
                        history.push("/sign-in");
                      },
                    },
                  ],
                }}
                trigger={["click"]}
                placement="bottomRight"
                getPopupContainer={() => document.body}
              >
                <Button
                  type="text"
                  className="mobile-header-profile-btn"
                  style={{ color: "#ffffff", minWidth: 40, minHeight: 40, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent" }}
                  aria-label="Profile"
                >
                  <UserOutlined style={{ fontSize: 20 }} />
                </Button>
              </Dropdown>
            </div>
          </AntHeader>

          {/* Location row - map pin + city, state */}
          <div className="mobile-location-row" style={{ background: "#0d1117", padding: "8px 16px 12px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 6 }}>
            <EnvironmentOutlined style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} />
            <span style={{ fontSize: 14, color: "#ffffff" }}>{mobileDealerLocation || "Location"}</span>
          </div>
          
          <Content className="content-ant main-content mobile-content" style={{ padding: "16px", paddingTop: "max(16px, env(safe-area-inset-top, 0px))", paddingLeft: "max(16px, env(safe-area-inset-left, 0px))", paddingRight: "max(16px, env(safe-area-inset-right, 0px))", background: "#0d0f1a", minHeight: "calc(100vh - 160px)", overflow: "visible", paddingBottom: 0 }}>
            <div className="mobile-content-inner">
              {children}
            </div>
            {/* Spacer so the last content can scroll above the fixed bottom nav */}
            <div className="mobile-bottom-spacer" aria-hidden="true" />
          </Content>
        </Layout>

        {/* Mobile bottom nav */}
        <nav className="mobile-bottom-nav" style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: "rgba(13, 15, 26, 0.98)", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-around", paddingBottom: "env(safe-area-inset-bottom)", zIndex: 100 }}>
          {navItems.map(({ path, label, icon: Icon }) => {
            const active = isActive(path);
            return (
              <button
                key={path}
                type="button"
                className={`mobile-bottom-nav-item ${active ? "mobile-bottom-nav-item-active" : ""}`}
                onClick={() => history.push(path)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, background: "none", border: "none", color: active ? "#9333ea" : "rgba(255,255,255,0.65)", padding: "8px 12px", cursor: "pointer", fontSize: 11, fontWeight: 500 }}
              >
                <Icon style={{ fontSize: 20, color: active ? "#9333ea" : "rgba(255,255,255,0.65)" }} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>

        <FeedbackModal
          open={showFeedbackModal}
          onCancel={() => setShowFeedbackModal(false)}
          onSend={() => setShowFeedbackModal(false)}
        />
      </Layout>
    );
  }

  // Desktop Layout - Account Settings page is not vertically scrollable
  const isAccountSettings = pathname === "/profile" || pathname === "/billing";

  return (
    <Layout className="theme-dark layout-dashboard" style={{ height: "100vh", overflow: "hidden" }}>
      {/* Desktop Sider */}
      <Sider
        width={200}
        theme="dark"
        className="desktop-sider"
        style={{
          background: "transparent",
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "auto",
          boxShadow: "inset -1px 0 0 rgba(147,51,234,0.12)",
        }}
      >
        <Sidenav />
      </Sider>

      {/* Desktop Content */}
      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        <AntHeader className="main-header">
          <Header name={getPageName(pathname)} />
        </AntHeader>
        <Content
          className="content-ant main-content"
          style={{
            overflowY: isAccountSettings ? "hidden" : "auto",
            overflowX: "hidden",
            height: "calc(100vh - 40px)",
            minHeight: "calc(100vh - 40px)",
          }}
        >
          <div
            className="main-content-inner"
            style={{
              minHeight: "100%",
              paddingBottom: isAccountSettings ? 0 : "80px",
            }}
          >
            {children}
          </div>
        </Content>
      </Layout>
      <FeedbackModal
        open={showFeedbackModal}
        onCancel={() => setShowFeedbackModal(false)}
        onSend={() => setShowFeedbackModal(false)}
      />
    </Layout>
  );
}

export default Main;